import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Palette, Check, Sun, Moon, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';

// Theme definitions with Goodwin brand colors
const THEMES = [
  {
    id: 'default',
    name: 'Goodwin Hospice',
    description: 'Brand default theme',
    preview: 'bg-gradient-to-r from-[#00b0ab] to-[#008a86]',
    class: '', // No additional class needed for default
  },
  {
    id: 'ocean',
    name: 'Ocean Blue',
    description: 'Modern & vibrant',
    preview: 'bg-gradient-to-r from-[#00b0ab] to-cyan-500',
    class: 'theme-ocean',
  },
  {
    id: 'emerald',
    name: 'Emerald Green',
    description: 'Fresh & calming',
    preview: 'bg-gradient-to-r from-emerald-500 to-teal-600',
    class: 'theme-emerald',
  },
  {
    id: 'sunset',
    name: 'Sunset Warm',
    description: 'Energetic & bold',
    preview: 'bg-gradient-to-r from-orange-500 to-amber-500',
    class: 'theme-sunset',
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Elegant dark theme',
    preview: 'bg-gradient-to-r from-[#00b0ab] to-slate-700',
    class: 'theme-midnight',
  },
  {
    id: 'coral',
    name: 'Coral Reef',
    description: 'Vibrant healthcare',
    preview: 'bg-gradient-to-r from-rose-500 to-[#00b0ab]',
    class: 'theme-coral',
  },
];

const DARK_MODES = [
  { id: 'light', name: 'Light', icon: Sun },
  { id: 'dark', name: 'Dark', icon: Moon },
  { id: 'system', name: 'System', icon: Monitor },
];

export function ThemeSelector({ variant = 'icon', showLabel = false }) {
  const [currentTheme, setCurrentTheme] = useState('default');
  const [darkMode, setDarkMode] = useState('light');

  // Load saved preferences on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('goodwin-theme') || 'default';
    const savedDarkMode = localStorage.getItem('goodwin-dark-mode') || 'light';
    setCurrentTheme(savedTheme);
    setDarkMode(savedDarkMode);
    applyTheme(savedTheme, savedDarkMode);
  }, []);

  const applyTheme = (themeId, darkModeId) => {
    const html = document.documentElement;
    
    // Remove all theme classes
    THEMES.forEach(theme => {
      if (theme.class) {
        html.classList.remove(theme.class);
      }
    });
    html.classList.remove('dark');
    
    // Apply new theme class
    const theme = THEMES.find(t => t.id === themeId);
    if (theme && theme.class) {
      html.classList.add(theme.class);
    }
    
    // Apply dark mode
    if (darkModeId === 'dark') {
      html.classList.add('dark');
    } else if (darkModeId === 'system') {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        html.classList.add('dark');
      }
    }
  };

  const handleThemeChange = (themeId) => {
    setCurrentTheme(themeId);
    localStorage.setItem('goodwin-theme', themeId);
    applyTheme(themeId, darkMode);
  };

  const handleDarkModeChange = (modeId) => {
    setDarkMode(modeId);
    localStorage.setItem('goodwin-dark-mode', modeId);
    applyTheme(currentTheme, modeId);
  };

  const currentThemeData = THEMES.find(t => t.id === currentTheme) || THEMES[0];
  const currentDarkModeData = DARK_MODES.find(m => m.id === darkMode) || DARK_MODES[0];

  if (variant === 'full') {
    return (
      <div className="space-y-6">
        {/* Color Theme Selection */}
        <div>
          <h3 className="text-sm font-medium mb-3">Color Theme</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {THEMES.map((theme) => (
              <button
                key={theme.id}
                onClick={() => handleThemeChange(theme.id)}
                className={cn(
                  "relative p-3 rounded-xl border-2 transition-all text-left",
                  currentTheme === theme.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                )}
              >
                <div className={cn("h-8 rounded-lg mb-2", theme.preview)} />
                <p className="text-sm font-medium">{theme.name}</p>
                <p className="text-xs text-muted-foreground">{theme.description}</p>
                {currentTheme === theme.id && (
                  <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-3 w-3 text-primary-foreground" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Dark Mode Selection */}
        <div>
          <h3 className="text-sm font-medium mb-3">Appearance</h3>
          <div className="flex gap-2">
            {DARK_MODES.map((mode) => (
              <Button
                key={mode.id}
                variant={darkMode === mode.id ? "default" : "outline"}
                size="sm"
                onClick={() => handleDarkModeChange(mode.id)}
                className="flex-1"
              >
                <mode.icon className="h-4 w-4 mr-2" />
                {mode.name}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Icon dropdown variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="theme-selector-btn">
          <Palette className="h-5 w-5" />
          <div className={cn("absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background", currentThemeData.preview)} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Color Theme</DropdownMenuLabel>
        {THEMES.map((theme) => (
          <DropdownMenuItem
            key={theme.id}
            onClick={() => handleThemeChange(theme.id)}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className={cn("h-5 w-5 rounded-full", theme.preview)} />
            <div className="flex-1">
              <p className="text-sm font-medium">{theme.name}</p>
            </div>
            {currentTheme === theme.id && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        {DARK_MODES.map((mode) => (
          <DropdownMenuItem
            key={mode.id}
            onClick={() => handleDarkModeChange(mode.id)}
            className="flex items-center gap-3 cursor-pointer"
          >
            <mode.icon className="h-4 w-4" />
            <span className="flex-1">{mode.name}</span>
            {darkMode === mode.id && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Hook for theme state (optional use)
export function useTheme() {
  const [theme, setTheme] = useState('default');
  const [darkMode, setDarkMode] = useState('light');

  useEffect(() => {
    setTheme(localStorage.getItem('goodwin-theme') || 'default');
    setDarkMode(localStorage.getItem('goodwin-dark-mode') || 'light');
  }, []);

  return { theme, darkMode, THEMES, DARK_MODES };
}

export default ThemeSelector;
